import axios from 'axios';

import { reportError } from './logError';
import { store } from '../app/store';

// Catches client-side JS errors that React error boundaries DON'T:
// uncaught exceptions (event handlers, async callbacks) and unhandled promise
// rejections. Reports who hit it and where, to the same CloudWatch stream the
// ErrorBoundary uses.

const loggedErrors = new Set<string>();
const MAX_DEDUP = 100;

/** Test-only: clears the dedup set. */
export function clearClientErrorDedup(): void {
  loggedErrors.clear();
}

function currentUserEmail(): string | undefined {
  try {
    return (store.getState() as { user?: { user?: { email?: string } } })?.user?.user?.email;
  } catch {
    return undefined;
  }
}

export function reportClientError(error: unknown, source: string): void {
  const err = error as { message?: string; stack?: string } | undefined;
  const message = err?.message ?? (typeof error === 'string' ? error : 'Unknown client error');

  // Dedup identical errors (per source) so a render loop can't flood CloudWatch.
  const key = `${source}|${message}`;
  if (loggedErrors.has(key)) return;
  if (loggedErrors.size < MAX_DEDUP) loggedErrors.add(key);

  const details = {
    type: 'client-error',
    source, // 'window.onerror' | 'unhandledrejection'
    message,
    stack: err?.stack ? err.stack.slice(0, 2000) : undefined,
    user: currentUserEmail(), // who
    location: window.location.href, // where
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    time: new Date().toISOString(),
  };

  const logGroup = 'tpn-client/error-handler';
  const logStream = `${process.env.REACT_APP_ENVIRONMENT?.toLowerCase() || 'dev'}-client-errors`;
  reportError(logGroup, logStream, details);
}

export function handleWindowError(event: ErrorEvent): void {
  reportClientError(event.error ?? { message: event.message }, 'window.onerror');
}

export function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  // HTTP errors are already reported by the axios response interceptor — skip
  // them here so they aren't double-logged.
  if (axios.isAxiosError(event.reason)) return;
  reportClientError(event.reason, 'unhandledrejection');
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}
